import { useContext, useEffect, useMemo, useState } from "react";
import {
  type ITreeMenu,
  CanAccess,
  useIsExistAuthentication,
  useTranslate,
  useLogout,
  useMenu,
  useWarnAboutChange,
  useNavigation,
} from "@refinedev/core";
import { Link } from "react-router-dom";
import { useUnreadConversations } from "./useUnreadConversations";
import { type Sider } from "@refinedev/antd";
import {
  Layout as AntdLayout,
  Menu,
  Grid,
  theme,
  Button,
  Space,
  Divider,
  Badge,
} from "antd";
import {
  LogoutOutlined,
  UnorderedListOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { antLayoutSider, antLayoutSiderMobile } from "./styles";
import {
  XconsIcon,
  CollapseOpen,
  CollapseClose,
  SellerIcon,
} from "@/components/icon";
import { DropMenuUser } from "./dropMenu/DropMenuUser";
import { ModalDeCambiosSinGuardar } from "@/components/unsaved-changes";
import { DropMenuAlert } from "./dropMenu/DropMenuAlert";
import { ResourcesSellers } from "@/config/SmartMenu/resourcesSellers";
import { ResourcesStores } from "@/config/SmartMenu/resourcesStores";
import { usePerfilDelMenu } from "@/config/resources";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import logo from "@/assets/img/logo_xcons.png";
import React from "react";
import { conversationsApi } from "@/services/conversations.api";

const { useToken } = theme;

export const CustomSider: typeof Sider = ({ render }) => {
  const { token } = useToken();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  // Badge de conversaciones sin leer (022 C7).
  const unreadConversations = useUnreadConversations();
  const { smartMenuState, setSmartMenuState, smartMenuName, smartMenuLabel, smartMenuType } =
    useContext(SmartMenuContext);
  // Para los ítems del menú del store que dependen de un permiso de ESE store (Categorías).
  const perfilDelMenu = usePerfilDelMenu();
  const isExistAuthentication = useIsExistAuthentication();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { mutate: mutateLogout } = useLogout();
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const [updatedSelectedKey, setUpdatedSelectedKey] = useState(selectedKey);
  const { SubMenu } = Menu;
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const { push } = useNavigation();

  const breakpoint = Grid.useBreakpoint();
  const smartMenuTypeLink = smartMenuType.replace(/"/g, "");
  const isMobile =
    typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  const goBackSmartMenu = () => {
    push(`/${encodeURIComponent(smartMenuTypeLink)}`);
    setSmartMenuState(false);
  };

  const renderTreeView = (tree: any, selectedKey: string) => {
    return tree.map((item: ITreeMenu) => {
      const { name, children, meta, key, list } = item;

      const icon = meta?.icon;
      const label = meta?.label ?? name;
      const parent = meta?.parent;
      const route =
        typeof list === "string"
          ? list
          : typeof list !== "function"
          ? list?.path
          : key;

      if (meta?.type === "group") {
        return (
          <React.Fragment key={`group-${key}`}>
            <span
              style={{
                margin: "32px 12px 14px",
                display: "block",
                color: token.colorTextSecondary,
                fontWeight: token.fontWeightStrong,
              }}
            >
              {!collapsed ? translate(label) ?? label : ""}
            </span>
            {renderTreeView(children, selectedKey)}
          </React.Fragment>
        );
      } else if (children.length > 0) {
        return (
          <SubMenu
            key={`${key}_${label}_item`}
            title={translate(label) ?? label}
          >
            {renderTreeView(children, selectedKey)}
          </SubMenu>
        );
      }

      const lastValue = "/" + selectedKey.split("/").pop();
      const isSelected = route === lastValue;
      const isRoute = !(parent !== undefined && children.length === 0);

      return (
        <React.Fragment key={`item-${key}`}>
          <CanAccess resource={name} action="list" params={{ resource: item }}>
            <Menu.Item
              key={`${key}`}
              icon={icon ?? (isRoute && <UnorderedListOutlined />)}
              className={isSelected ? "ant-menu-item-selected" : ""}
            >
              {route ? (
                <Link to={route || "/"} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{translate(label) ?? label}</span>
                  {/* Badge de no leídas (022 C7): el layout es el de esta rama; el número sale del
                      hook real (endpoint + WebSocket), no del contador hardcodeado. */}
                  {name === "conversations" && unreadConversations > 0 && !collapsed && (
                    <Badge count={unreadConversations} style={{ backgroundColor: token.colorTextTertiary }} />
                  )}
                </Link>
              ) : (
                translate(label) ?? label
              )}
              {!collapsed && isSelected && (
                <div className="ant-menu-tree-arrow" />
              )}
            </Menu.Item>
          </CanAccess>
        </React.Fragment>
      );
    });
  };

  const renderChildrenTreeView = (tree: any, selectedKey: string) => {
    return tree.map((item: any) => {
      const { name, key, meta, list, children = [], items = [] } = item;

      const icon = meta?.icon;
      const label = meta?.label ?? name;
      const route = typeof list === "string" ? list : list?.path;

      const hasChildren = children.length > 0 || items.length > 0;
      const subItems = children.length > 0 ? children : items;

      const lastValue = "/" + selectedKey.split("/").pop();
      const isSelected = route === lastValue;

      if (hasChildren) {
        return (
          <SubMenu
            key={`submenu_${key}`}
            title={translate(label) ?? label}
            icon={icon || null}
          >
            {renderChildrenTreeView(subItems, selectedKey)}
          </SubMenu>
        );
      }

      return (
        <CanAccess
          key={key}
          resource={label}
          action="list"
          params={{ resource: item }}
        >
          <Menu.Item key={`menu_${key}`} icon={icon}>
            <Link to={route || "#"}>{translate(label) ?? label}</Link>
            {!collapsed && isSelected && (
              <div className="ant-menu-tree-arrow" />
            )}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  /** Con cambios sin guardar, el popup del admin y no el del navegador (xcons-docs#490). */
  const [cerrandoSesion, setCerrandoSesion] = useState(false);
  const handleLogout = () => {
    if (warnWhen) setCerrandoSesion(true);
    else mutateLogout();
  };
  const avisoAlCerrarSesion = (
    <ModalDeCambiosSinGuardar
      open={cerrandoSesion}
      textoSalir="Cerrar sesión sin guardar"
      onQuedarse={() => setCerrandoSesion(false)}
      onSalir={() => {
        setCerrandoSesion(false);
        setWarnWhen(false);
        mutateLogout();
      }}
    />
  );

  const logout = isExistAuthentication && (
    <Menu.Item key="logout" onClick={handleLogout} icon={<LogoutOutlined />}>
      {translate("buttons.logout", "Logout")}
    </Menu.Item>
  );

  const items = renderTreeView(menuItems, selectedKey);

  const renderSider = (): React.ReactNode => {
    if (render) {
      return render({
        dashboard: null,
        items,
        logout,
        collapsed,
      });
    }
    return (
      <>
        {items}
        {/* {logout} */}
      </>
    );
  };

  const smartItems = renderChildrenTreeView(
    smartMenuTypeLink === "sellers"
      ? ResourcesSellers(t, perfilDelMenu)
      : ResourcesStores(t, perfilDelMenu),
    selectedKey,
  );

  const renderChildrenSider = (): React.ReactNode => {
    return <>{smartItems}</>;
  };

  useEffect(() => {
    if (smartMenuState && selectedKey === "/administrator/sellers") {
      setUpdatedSelectedKey("linked_products");
    } else {
      const keyWithoutSlashes = selectedKey.replace(/^\/|\/[^\/]*$/g, "");
      setUpdatedSelectedKey(smartMenuState ? keyWithoutSlashes : selectedKey);
    }
  }, [selectedKey, smartMenuState, updatedSelectedKey]);

  const siderStyle = isMobile ? antLayoutSiderMobile : antLayoutSider;

  const renderSpace = () => {
    return (
      <Space
        size="middle"
        direction={collapsed ? "vertical" : "horizontal"}
        style={{
          width: collapsed ? "80px" : "235px",
          padding: collapsed ? "0" : "0 16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "64px",
          backgroundColor: token.colorBgElevated,
          fontSize: "14px",
          position: "fixed",
          bottom: collapsed ? "80px" : "45px",
          borderTop: !collapsed ? "1px solid #F0F0F0" : undefined,
          gap: !collapsed ? "10px" : "20px",
        }}
      >
        {!collapsed ? (
          <>
            <DropMenuUser collapsed={collapsed} />
            <Divider style={{ margin: 0, height: "32px" }} type="vertical" />
            <DropMenuAlert collapsed={collapsed} />
          </>
        ) : (
          <>
            <DropMenuAlert collapsed={collapsed} />
            <DropMenuUser collapsed={collapsed} />
          </>
        )}
      </Space>
    );
  };

  const menuIcon = useMemo(() => {
    switch (smartMenuTypeLink) {
      case "sellers":
        return (
          <SellerIcon
            style={{ color: token.colorTextSecondary, transform: "scale(1.4)" }}
          />
        );
      case "stores":
        return <ShopOutlined style={{ color: token.colorTextSecondary }} />;
      default:
        return (
          <span style={{ color: token.colorTextSecondary, fontSize: "12px" }}>
            smartMenuTypeLink
          </span>
        );
    }
  }, [smartMenuType]);

  return (
    <AntdLayout.Sider
      theme="light"
      width={"235px"}
      collapsible
      collapsedWidth={isMobile ? 0 : 80}
      collapsed={collapsed}
      breakpoint="lg"
      onCollapse={(collapsed: boolean): void => setCollapsed(collapsed)}
      style={{
        ...siderStyle,
        backgroundColor: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBgElevated}`,
        width: "400px",
      }}
      trigger={
        !isMobile && (
          <Button
            type="text"
            style={{
              borderRadius: 0,
              height: "100%",
              width: "100%",
              borderTop: "1px solid #F0F0F0",
              backgroundColor: token.colorBgElevated,
            }}
          >
            {collapsed ? (
              <div
                style={{
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  backgroundColor: token.colorBgElevated,
                }}
              >
                <span>{i18n.language === "es" ? "Más" : "More"}</span>{" "}
                <CollapseClose />
              </div>
            ) : (
              <div
                style={{
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  backgroundColor: token.colorBgElevated,
                }}
              >
                <span>{i18n.language === "es" ? "Menos" : "Less"}</span>{" "}
                <CollapseOpen />
              </div>
            )}
          </Button>
        )
      }
    >
      {/* El popup de cerrar sesión: se dibuja aparte, encima de todo. */}
      {avisoAlCerrarSesion}
      <div
        style={{
          width: collapsed ? "80px" : "235px",
          padding: collapsed ? "0" : "0 16px",
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-start",
          alignItems: "center",
          height: "64px",
          backgroundColor: token.colorBgElevated,
          fontSize: "14px",
          position: "sticky",
          top: 10,
        }}
      >
        {smartMenuState ? (
          !collapsed ? (
            <div
              style={{
                borderBottom: "1px solid #F0F0F0",
                width: "100%",
                display: "flex",
                alignItems: "center",
                height: "62px",
              }}
            >
              <Button
                type="link"
                style={{ color: "black" }}
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                  goBackSmartMenu();
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginLeft: "18px",
                }}
              >
                {menuIcon}
                <span
                  style={{
                    textTransform: "capitalize",
                    fontWeight: "bold",
                    color: token.colorTextSecondary,
                  }}
                >
                  {(smartMenuLabel || smartMenuName).replace(/"/g, "")}
                </span>
              </div>
            </div>
          ) : (
            <div
              style={{
                borderBottom: "1px solid #F0F0F0",
                display: "flex",
                alignItems: "center",
                height: "62px",
              }}
            >
              <Button
                type="link"
                style={{ color: "black" }}
                icon={<ArrowLeftOutlined />}
                onClick={() => goBackSmartMenu()}
              />
              <div style={{ display: "flex", alignItems: "center" }}>
                {menuIcon}
              </div>
            </div>
          )
        ) : !collapsed ? (
          <img src={logo} alt="Logo Xcons" style={{ width: "100%" }} />
        ) : (
          <XconsIcon />
        )}
      </div>
      <Menu
        defaultOpenKeys={defaultOpenKeys}
        selectedKeys={[updatedSelectedKey]}
        mode="inline"
        style={{
          marginTop: "8px",
          border: "none",
          position: "sticky",
          top: 72,
        }}
        onClick={(e) => {
          if (!breakpoint.lg) {
            setCollapsed(true);
          }
        }}
      >
        {!smartMenuState ? renderSider() : (renderChildrenSider() as any)}

        {!collapsed && renderSpace()}

        {!isMobile && renderSpace()}
      </Menu>
    </AntdLayout.Sider>
  );
};
